import { setOptions, importLibrary } from '@googlemaps/js-api-loader';

// Browser key from the ACF options (see Site::addScripts).
setOptions({
    key: window.fadoq_maps?.api_key,
});

const DEFAULT_COOKIE_LIFETIME = 2592000; // 30 days, in seconds

function setCookie(name, value, lifetime = DEFAULT_COOKIE_LIFETIME) {
    const expires = new Date(Date.now() + lifetime * 1000).toUTCString();
    const secure = window.location.protocol === 'https:' ? '; secure' : '';
    document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires}; path=/; samesite=lax${secure}`;
}

// Reload the page, or go to redirectUrl when given (e.g. to drop search parameters).
function reloadPage(redirectUrl) {
    if (!redirectUrl) {
        window.location.reload();
        return;
    }

    const target = new URL(redirectUrl, window.location.href);
    const current = new URL(window.location.href);
    target.hash = '';
    current.hash = '';

    // Same URL apart from the hash: assigning it would only scroll, not reload.
    if (target.href === current.href) {
        window.location.reload();
    } else {
        window.location.assign(redirectUrl);
    }
}

let geocodingLibrary;

function loadGeocodingLibrary() {
    if (!geocodingLibrary) {
        geocodingLibrary = importLibrary('geocoding').catch((error) => {
            geocodingLibrary = null;
            throw error;
        });
    }

    return geocodingLibrary;
}

if (document.body?.classList.contains('theme-fadoq')) {
    loadGeocodingLibrary().catch(() => {});
}

async function setRegion(postalCode) {
    const regionResponse = await fetch(window.ajax_script?.ajaxurl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: `action=get_region_by_postal_code&postal_code=${postalCode.replace(/\s/g, '')}&nonce=${window.ajax_script?.nonce}`,
    });

    const regionJson = await regionResponse.json();

    if (!regionJson.success) {
        const serverError = document.querySelector('.geolocation-panel-error.server');
        if (serverError) serverError.hidden = false;
        return false;
    }

    setCookie('fadoq_region', regionJson.data.region_slug);
    return true;
}

async function geocode(request) {
    const { Geocoder } = await loadGeocodingLibrary();
    const { results } = await new Geocoder().geocode(request);

    return results;
}

export function setCoordinatesCookie(lat, lng) {
    setCookie('fadoq_coordinates', `${lat},${lng}`);
}

async function setLocation({
    postalCode, lat, lng, redirectUrl, onSuccess,
}) {
    const request = postalCode ? { componentRestrictions: { country: 'CA', postalCode } } : { location: { lat, lng } };

    const [result] = await geocode(request).catch(() => []);

    const coordinates = postalCode ? result?.geometry.location.toJSON() : { lat, lng };
    const regionPostalCode = postalCode ?? result?.address_components.find((address) => address.types.includes('postal_code'))?.short_name;

    if (!regionPostalCode) {
        throw new Error('No postal code found for this position');
    }

    if (!(await setRegion(regionPostalCode))) {
        return;
    }

    if (coordinates) {
        setCoordinatesCookie(coordinates.lat, coordinates.lng);
    } else {
        setCookie('fadoq_coordinates', '', -1);
    }

    onSuccess?.();
    reloadPage(redirectUrl);
}

/**
 * Geocode a typed address (restricted to Canada).
 *
 * @returns {Promise<{lat: number, lng: number}>} Rejects when the address is not found.
 */
export async function geocodeAddress(address) {
    const [result] = await geocode({ address, componentRestrictions: { country: 'CA' } });

    if (!result) {
        throw new Error('No geocoding result for this address');
    }

    return result.geometry.location.toJSON();
}

export async function setRegionByPostalCode(text) {
    await setLocation({ postalCode: text.replace(/\s/g, '') });
}

/**
 * Locate the visitor, store their coordinates and region, then reload the page
 * (or go to redirectUrl when given, e.g. to drop search parameters).
 */
export default function geolocate({ onError, onSuccess, redirectUrl } = {}) {
    if (!document.body.classList.contains('theme-fadoq')) {
        return;
    }

    if (!navigator.geolocation) {
        onError?.();
        return;
    }

    navigator.geolocation.getCurrentPosition(
        async ({ coords }) => {
            try {
                await setLocation({
                    lat: coords.latitude, lng: coords.longitude, redirectUrl, onSuccess,
                });
            } catch (error) {
                onError?.(error);
            }
        },
        (error) => onError?.(error),
    );
}
